import {
  defaultTimeoutFor,
  STREAM_STALL_TIMEOUT_MS,
  useThreadActivityTimeout,
} from "@lib/hooks/useThreadActivityTimeout";
import { useChatStore } from "@lib/stores/chatStore";
import { track } from "@lib/telemetry";
import type {
  ChatAttachment,
  ChatRichTextArtefact,
  ChatThread as ChatThreadType,
} from "@lib/types";
import { ArrowLeft, Bug, ChevronDown, ChevronRight, Loader2, Square } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { ChatInput } from "./ChatInput";
import { ChatMessage } from "./ChatMessage";
import { ContextUsageBar, ContextWarningBanner } from "./ContextUsageBar";
import { PlanCard } from "./PlanCard";
import { PlanModeToggle } from "./PlanModeToggle";
import { QuestionCard } from "./QuestionCard";
import { TaskListCard } from "./TaskListCard";

interface ChatThreadProps {
  thread: ChatThreadType;
}

export function ChatThreadView({ thread }: ChatThreadProps) {
  const messages = useChatStore((s) => s.messages);
  const isSending = useChatStore((s) => s.isSending);
  const sendMessage = useChatStore((s) => s.sendMessage);
  const interruptThread = useChatStore((s) => s.interruptThread);
  const compactThread = useChatStore((s) => s.compactThread);
  const closeCurrentThread = useChatStore((s) => s.closeCurrentThread);
  const resyncThread = useChatStore((s) => s.resyncThread);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [isCompacting, setIsCompacting] = useState(false);
  const [showCompacted, setShowCompacted] = useState(false);

  const setPlanModeEnabled = useChatStore((s) => s.setPlanModeEnabled);
  const activePlan = useChatStore((s) => s.plansByThread[thread.id] ?? null);
  const pendingQuestion = useChatStore((s) => s.questionsByThread[thread.id] ?? null);
  const taskList = useChatStore((s) => s.taskListByThread[thread.id] ?? null);
  // The reply currently streaming in (#3162); replaced by the persisted
  // message when the turn's new_message broadcast arrives.
  const streaming = useChatStore((s) => s.streamingByThread[thread.id] ?? null);
  const streamingText = streaming?.text ?? "";

  const isAwaitingUser =
    thread.current_phase === "awaiting_plan_approval" ||
    thread.current_phase === "awaiting_step_continuation" ||
    thread.current_phase === "awaiting_user_answer";
  const isProcessing = thread.status === "active" && thread.current_phase !== "idle";
  const showRunningSpinner = isProcessing && !isAwaitingUser;
  // Threads snapshot their model at creation; once the organisation's fixed
  // model changes they are kept for history but cannot be continued.
  const modelChanged = thread.continuable === false;
  const canSend = !isSending && !isProcessing && !modelChanged;

  const awaitingHint = (() => {
    switch (thread.current_phase) {
      case "awaiting_plan_approval":
        return "Approve, reject, or revise the plan above to continue.";
      case "awaiting_user_answer":
        return "Answer the question above to continue.";
      case "awaiting_step_continuation":
        return "Run the next step or cancel the plan above to continue.";
      default:
        return null;
    }
  })();

  const { timedOut } = useThreadActivityTimeout({
    threadId: thread.id,
    status: thread.status,
    phase: thread.current_phase,
    heartbeat: streaming?.lastDeltaAt,
    // Catch up silently first; the manual affordance only shows if the turn
    // is still parked in the same phase afterwards.
    onTimeout: () => {
      track("timeout_tripped", {
        thread_id: thread.id,
        phase: thread.current_phase,
        waited_ms: streaming ? STREAM_STALL_TIMEOUT_MS : defaultTimeoutFor(thread.current_phase),
      });
      return resyncThread(thread.id);
    },
  });

  const latestInputTokens = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (m.role === "assistant" && m.input_tokens && m.input_tokens > 0) {
        return m.input_tokens;
      }
    }
    return 0;
  }, [messages]);

  const { compactedMessages, visibleMessages, latestToolUseLabel } = useMemo(() => {
    const compacted = messages.filter((m) => m.compacted);
    // tool_use and tool_result rows are surfaced at the thread level (the
    // activity indicator at the bottom shows the current tool's label) rather
    // than as persistent transcript entries.
    const visible = messages.filter((m) => !m.compacted && !m.tool_result && !m.tool_use);
    let label: string | null = null;
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (m.tool_use && m.content) {
        label = m.content;
        break;
      }
    }
    return { compactedMessages: compacted, visibleMessages: visible, latestToolUseLabel: label };
  }, [messages]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: scroll on message/phase/stream changes
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isProcessing, streamingText]);

  function handleSend(
    message: string,
    attachments: ChatAttachment[],
    richTextArtefacts: ChatRichTextArtefact[],
  ) {
    sendMessage(thread.id, message, attachments, richTextArtefacts);
  }

  async function handleCompact() {
    setIsCompacting(true);
    try {
      await compactThread(thread.id);
    } finally {
      setIsCompacting(false);
    }
  }

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* Header */}
      <div className="flex items-center gap-2 border-b border-border px-3 py-2">
        <button
          type="button"
          onClick={closeCurrentThread}
          className="text-text-tertiary hover:text-text-primary"
        >
          <ArrowLeft size={16} />
        </button>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-medium text-text-primary truncate">{thread.title}</h3>
        </div>
        {latestInputTokens > 0 && <ContextUsageBar inputTokens={latestInputTokens} />}
        <PlanModeToggle
          threadId={thread.id}
          enabled={thread.plan_mode_enabled ?? false}
          onChange={({ enabled }) => setPlanModeEnabled(thread.id, enabled)}
        />
        <a
          href={`/app/chat/threads/${thread.id}/debug`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-text-tertiary hover:text-text-primary"
          title="Debug view"
        >
          <Bug size={14} />
        </a>
      </div>
      {latestInputTokens > 0 && (
        <ContextWarningBanner
          inputTokens={latestInputTokens}
          onCompact={handleCompact}
          isCompacting={isCompacting}
        />
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        {/* Compacted messages section */}
        {compactedMessages.length > 0 && (
          <div className="border border-border rounded-md overflow-hidden">
            <button
              type="button"
              onClick={() => setShowCompacted(!showCompacted)}
              className="w-full flex items-center gap-1.5 px-3 py-1.5 text-xs text-text-tertiary hover:bg-surface-secondary"
            >
              {showCompacted ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
              <span>
                {compactedMessages.length} compacted message{compactedMessages.length !== 1 && "s"}
              </span>
            </button>
            {showCompacted && (
              <div className="border-t border-border p-3 space-y-3 bg-surface-secondary/50 opacity-60">
                {compactedMessages.map((message) => (
                  <ChatMessage key={message.id} message={message} />
                ))}
              </div>
            )}
          </div>
        )}

        {visibleMessages.map((message) => (
          <ChatMessage key={message.id} message={message} />
        ))}

        {streaming && streamingText && (
          <ChatMessage
            key={streaming.messageId}
            message={{
              id: streaming.messageId,
              thread_id: thread.id,
              role: "assistant",
              content: streamingText,
              created_at: "",
            }}
          />
        )}

        {showRunningSpinner && (
          <div className="flex flex-col gap-1 py-1 text-xs text-text-tertiary">
            <div className="flex items-center gap-2">
              <Loader2 size={14} className="animate-spin" />
              <span>
                {thread.current_phase === "queued" && "Waiting for a worker..."}
                {thread.current_phase === "executing_tool" && (latestToolUseLabel ?? "Running...")}
                {thread.current_phase === "thinking" &&
                  (streamingText ? "Responding..." : "Thinking...")}
                {thread.current_phase === "responding" && "Responding..."}
              </span>
              <button
                type="button"
                onClick={() => interruptThread(thread.id)}
                className="ml-auto text-text-tertiary hover:text-error"
                title="Interrupt"
              >
                <Square size={12} />
              </button>
            </div>
            {timedOut && (
              <div className="flex items-center gap-2 pl-6">
                <span>This is taking longer than expected.</span>
                <button
                  type="button"
                  onClick={() => {
                    track("refresh_clicked", { thread_id: thread.id, phase: thread.current_phase });
                    void resyncThread(thread.id);
                  }}
                  className="underline hover:text-text-primary"
                >
                  Refresh?
                </button>
              </div>
            )}
          </div>
        )}
        {taskList && taskList.length > 0 && <TaskListCard tasks={taskList} />}
        {activePlan && <PlanCard plan={activePlan} />}
        {pendingQuestion && pendingQuestion.state === "open" && (
          <QuestionCard question={pendingQuestion} />
        )}
        {awaitingHint && (
          <div className="py-1 text-xs italic text-text-tertiary">{awaitingHint}</div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {modelChanged && (
        <div className="border-t border-border bg-surface-secondary px-4 py-2 text-xs text-text-tertiary">
          This conversation used a model your organisation no longer uses. Start a new chat to
          continue.
        </div>
      )}

      {/* Input */}
      <ChatInput onSend={handleSend} disabled={!canSend} />
    </div>
  );
}
